import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider.tsx'
import { publicOrigin } from '../platform/origin.ts'
import { Banner, Field, PasswordInput } from '../ui/components.tsx'
import { lastLogo } from '../club/branding.ts'
import { CourseScene, Emblem } from '../ui/Emblem.tsx'

/** What a refused sign-in says (STACK 9.5): no answer -> the network; too many attempts -> wait; a native build without the club's address -> says so;
 *  the server unreachable behind the proxy (502-504) -> the network too; anything else -> the one "wrong address or password" (the server gives one code
 *  for every refusal, on purpose). */
export function signInErrorText(t: (k: string) => string, r: { error?: string; status?: number }): string {
  if (r.error === 'no_public_origin') return t('errors.noPublicOrigin')
  if (r.status === 429 || r.error === 'rate_limited') return t('auth.rateLimited')
  if (r.error === 'network' || r.error === 'timeout' || r.status === 502 || r.status === 503 || r.status === 504) return t('errors.network')
  return t('auth.invalid')
}

export function LoginPage() {
  const { t } = useTranslation()
  const { signIn } = useAuth()
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null); const [busy, setBusy] = useState(false)
  const [logo] = useState(lastLogo)                    // the club this device last belonged to: the sign-in page does not know the club yet
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null)
    const r = await signIn(email.trim(), password)
    if (r.error) setError(signInErrorText(t, r))
    setBusy(false)
  }
  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-5 p-6">
      <div className="turf relative overflow-hidden rounded-3xl px-5 pb-16 pt-6 text-white shadow-md" data-testid="login-hero">
        <CourseScene className="absolute inset-x-0 bottom-0 h-16 w-full opacity-60" />
        <div className="relative flex items-center gap-4">
          {logo
            ? <span className="inline-flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-1 ring-2 ring-white/70"><img src={logo} alt="" className="size-full object-contain" data-testid="login-logo" /></span>
            : <Emblem className="size-20 shrink-0" />}
          <div className="min-w-0"><h1 className="font-serif text-2xl font-bold">{t('app.name')}</h1><p className="text-white/85">{t('app.tagline')}</p></div>
        </div>
      </div>
      {!publicOrigin() && <Banner tone="error"><span data-testid="login-no-origin">{t('errors.noPublicOrigin')}</span></Banner>}
      <form onSubmit={submit} className="flex flex-col gap-4" aria-label={t('auth.title')}>
        <Field label={t('auth.email')} htmlFor="email"><input id="email" className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label={t('auth.password')} htmlFor="password"><PasswordInput id="password" autoComplete="current-password" required value={password} onChange={setPassword} /></Field>
        {error && <Banner tone="error">{error}</Banner>}
        <button className="btn btn-primary" disabled={busy}>{t('auth.signIn')}</button>
      </form>
      <p className="text-center text-sm opacity-70" data-testid="login-forgot">{t('auth.forgotPassword')}</p>
      <p className="text-center text-sm opacity-70">{t('auth.inviteOnly')}</p>
      <a href="/privacy" className="inline-flex min-h-14 items-center justify-center text-center text-base font-semibold underline underline-offset-4" data-testid="login-privacy">{t('auth.privacy')}</a>
    </main>
  )
}

export function MfaChallengePage() {
  const { t } = useTranslation()
  const { verifyMfa, signOut } = useAuth()
  const [code, setCode] = useState(''); const [error, setError] = useState<string | null>(null); const [busy, setBusy] = useState(false)
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null)
    const r = await verifyMfa(code.replace(/\s/g, ''))
    if (r.error) setError(t('auth.mfaInvalid'))
    setBusy(false)
  }
  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-5 p-6">
      <h1 className="text-xl font-bold">{t('auth.mfaTitle')}</h1>
      <p>{t('auth.mfaHelp')}</p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('auth.mfaCode')} htmlFor="code"><input id="code" className="input text-center text-2xl tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={7} required value={code} onChange={(e) => setCode(e.target.value)} /></Field>
        {error && <Banner tone="error">{error}</Banner>}
        <button className="btn btn-primary" disabled={busy}>{t('auth.mfaVerify')}</button>
      </form>
      <button className="btn" onClick={() => void signOut()}>{t('auth.signOut')}</button>
    </main>
  )
}
